
import type {PartSelectableObject} from "./partSelOb";
import {MathUtils} from "./mathUtils";
import {CONSTANTS} from "./meta_data";
import {Strecke, Vector2} from "./delme_globals";
import DEBUG from "./DEBUG";
import {logIfDebug} from "../global/logFunctions";
export interface PortallinieDataParameter {
    partname;
    pointA;
    pointB;
    stauchung;
}


export class PortalLinieAufPart {
    public partName: string;
    public pointA;
    public pointB;
    public angle;
    public stauchung;


    public constructor(partName:string, strecke:Strecke, stauchung:number) {
        this.partName = partName;
        this.pointA = strecke.start;
        this.pointB = strecke.end;
        this.stauchung = stauchung;

        this.pointA.uncompressed = {
            x: this.pointA.x / stauchung,
            y: this.pointA.y
        };
        this.pointB.uncompressed = {
            x: this.pointB.x / stauchung,
            y: this.pointB.y
        };

        if (this.pointA.x === this.pointB.x) {
            this.angle = 0;
        } else {
            if (this.pointA.x < this.pointB.x) {
                this.angle = Math.atan((this.pointA.x - this.pointB.x) / stauchung / (this.pointA.y - this.pointB.y));
            } else {
                this.angle = Math.atan((this.pointB.x - this.pointA.x) / stauchung / (this.pointB.y - this.pointA.y));
            }
        }
    }
}


export class PortalLine {
    portalLinieA:PortalLinieAufPart;
    portalLinieB:PortalLinieAufPart;
    portal:PortalLinieAufPart[];

    constructor(portallinieDataA:PortalLinieAufPart, portallinieDataB:PortalLinieAufPart) {
        this.portalLinieA = portallinieDataA;
        this.portalLinieB = portallinieDataB;
        this.portal = [this.portalLinieA, this.portalLinieB];
    }

    public intersectsObjecctWithSeperateTranslation(partSelOb, translation:Vector2) {

        return this.portal.reduce(function(carry, line) {
            if(carry === true) {
                return carry;
            }

            return PortalLine.intersectsPortalLineWithTranslation(partSelOb, line, translation);
        }, false);
    }

    public getSourceAndTargetportallineOnPartBypartnameWithoutCheckingForIntersection(partName:string): { source:PortalLinieAufPart, target:PortalLinieAufPart }|null {
        if(!this.presentOnPart(partName)) {
            logIfDebug("you queried the wrong portalline");
            return null;
        }
        if(this.portalLinieA.partName === partName) {
            return {
                source:this.portalLinieA,
                target:this.portalLinieB
            }
        } else {
            return {
                source:this.portalLinieB,
                target:this.portalLinieA
            }
        }
    }

    private getPortalLinieAufPart(partname) {
        if(!this.presentOnPart(partname)) {
            logIfDebug("you queried the wrong portalline");
            return null;
        }
        return this.portalLinieA.partName === partname ? this.portalLinieA : this.portalLinieB;
    }

    public getMiddlePointOfPortalline(partname:string):Vector2|null {
        const portallinieAufPart = this.getPortalLinieAufPart(partname);
        if(portallinieAufPart === null) {
            return null;
        }

        function getMiddle(portallinieAufPart : PortalLinieAufPart, dim:"x"|"y") {
            return (portallinieAufPart.pointA[dim] + portallinieAufPart.pointB[dim]) / 2;
        }
        return new Vector2(getMiddle(portallinieAufPart, "x"), getMiddle(portallinieAufPart, "y"));
    }

    /**
     * Checkt, ob ein Objekt von dieser PortalLinie geschnitten wird
     */
    public intersectsObject(partSelOb:PartSelectableObject) {
        return this.intersectsObjecctWithSeperateTranslation(partSelOb, partSelOb.translation);
    }

    private static unCompressVectorInXRichtung(vec, stauchung):Vector2 {
        return new Vector2(vec.x / stauchung, vec.y);
    }

    public calculateCloneValues(partSelOb:PartSelectableObject) {
        return this.calculateCloneValuesWithSeperateTranslation(partSelOb, partSelOb.translation)
    }

    public static getClonedTranslation(translation:Vector2, sourcePortalLinie:PortalLinieAufPart, targetPortalLinie:PortalLinieAufPart) {
        const ucSelObTranslation = PortalLine.unCompressVectorInXRichtung(translation, sourcePortalLinie.stauchung);
        let pointAtoCenter = new Vector2(
            ucSelObTranslation.x - sourcePortalLinie.pointA.uncompressed.x,
            ucSelObTranslation.y - sourcePortalLinie.pointA.uncompressed.y
        );

        pointAtoCenter = MathUtils.rotateVector(pointAtoCenter, -(targetPortalLinie.angle - sourcePortalLinie.angle));
        return new Vector2(
            targetPortalLinie.pointA.x + pointAtoCenter.x * targetPortalLinie.stauchung,
            targetPortalLinie.pointA.y + pointAtoCenter.y
        );
    }

    public calculateCloneValuesWithSeperateTranslation(partSelOb, translation:Vector2):{id,part,translation, rotation, scaling, cloneCloningAngleRad, origCloningAngleRad}|null {

        let targetPortalLinie, sourcePortalLinie;

        // Beni 17.03.2015:
        // Hier haben wir bisher ein Problem, da hier nicht berücksichtigen, dass eine Portallinie aufs selbe Parts spiegeln kann.
        // Ich prüfe jetzt nicht nur den Partnamen sondern auch ob auch Portallinie 0 oder 1 geschnitten wird.
        if (PortalLine.intersectsPortalLineWithTranslation(partSelOb, this.portalLinieA, translation)) {
            sourcePortalLinie = this.portalLinieA;
            targetPortalLinie = this.portalLinieB;
        } else if (PortalLine.intersectsPortalLineWithTranslation(partSelOb, this.portalLinieB, translation)) {
            sourcePortalLinie = this.portalLinieB;
            targetPortalLinie = this.portalLinieA;
        } else {
            if (DEBUG) {
                console.warn("calculate clone Values sagt: Vorsicht: Portallinie wurde nicht geschnitten");
            }
            return null;
        }

        const cloneTranslation = PortalLine.getClonedTranslation(translation, sourcePortalLinie, targetPortalLinie)

        const cloneRotation = partSelOb.rotation + (targetPortalLinie.angle - sourcePortalLinie.angle) * 180 / Math.PI;

        return {
            id: partSelOb.id,
            part: targetPortalLinie.partName,
            translation: cloneTranslation,
            rotation: cloneRotation,
            scaling: partSelOb.scaling,
            cloneCloningAngleRad: targetPortalLinie.angle,
            origCloningAngleRad: sourcePortalLinie.angle
        };
    }


    /**
     * Snaplinien (nur von Objekten, Parts werden extra gesetzt) werden durch PortalLinien weitergeführt
     * @param {string}              origPartName    Name des Parts, auf dem das Original liegt
     * @param {object}              pointEdgePairs  Geradendefinierende Punkte mit Edges ("bottom" etc)
     * @property {object[]}         points          Array mit Geraden definierenden Punkten mit x y Koord.
     * @prperty  {string}           edge            kante, die Snappen soll wie "bottom" etc
     * @param {string}              id
     *
     */
    public cloneSnapLines(origPartName, pointEdgePairs, id) {

        //Es macht nur Sinn, wenn ich gerade Snaplinien mit der Portallinie scheide,
        //bei "Kurven" wüsste ich ja nicht, wie ich sie fortsetzen sollte...
        const returnArray:{partName, pointEdgePair, id}[] = [];
        const part = (origPartName === this.portalLinieA.partName ? this.portalLinieA : this.portalLinieB);
        const otherPart = (origPartName === this.portalLinieA.partName ? this.portalLinieB : this.portalLinieA);

        for (let i = 0, len = pointEdgePairs.length; i < len; i++) {
            const result = MathUtils.checkLineIntersection(
                part.pointA,
                part.pointB,
                pointEdgePairs[i].points[0],
                pointEdgePairs[i].points[1]);
            if (result.onLine1 && result.onLine2) {
                //wir haben also einen Snap, der sich mit dieser Portallinie schneidet.
                //FIXME nicht Wolfsrudelgerecht! das hier ist Code für gerade Portallinien! Aber erst mal solls funktionieren.
                returnArray.push({partName: otherPart.partName, pointEdgePair: [pointEdgePairs[i]], id: id});

            }
        }
        return returnArray;
    }

    public intersectionPoint(snapLine) {
        const part = (snapLine.partName === this.portalLinieA.partName ? this.portalLinieA : this.portalLinieB);
        const result = MathUtils.checkLineIntersection(
            part.pointA,
            part.pointB,
            snapLine.points[0],
            snapLine.points[1],
        );

        if (result.onLine1 && result.onLine2) {
            return {x: result.x, y: result.y};
        }
        return null;
    }

    public getComplimentaryPart(origPartName) {
        if (this.portalLinieA.partName !== origPartName && this.portalLinieB.partName !== origPartName) {
            return null;
        }
        return (this.portalLinieA.partName === origPartName ? this.portalLinieB.partName : this.portalLinieA.partName); //der andere Part!!
    }

    public getPartNames = ()=>[this.portalLinieA.partName, this.portalLinieB.partName];
    public presentOnPart = partName=>[this.portalLinieA.partName, this.portalLinieB.partName].indexOf(partName)>-1;


    private static intersectsPortalLineWithTranslation(partSelOb:PartSelectableObject, portallinie, translation:Vector2) {
        const dimensions = partSelOb.boundingBoxDimensions();
        if(partSelOb.getParentPartName() !== portallinie.partName) {
            return false;
        }
        const box = {
            translation:translation,
            width:dimensions.width + 2 * CONSTANTS.HANDLER_WIDTH * partSelOb.getParent().partStauchungsFaktor,
            height:dimensions.height + 2 * CONSTANTS.HANDLER_HEIGHT
        }
        return MathUtils.lineIntersectsBox(portallinie, box);
    }

    public isVertical = ()=>this.portalLinieA.angle === 0;

    public xValueIsLeftOfOneLine(partname, x) {
        return this.portal.reduce(function(carry, l) {
            return carry || (l.partName === partname && (x < l.pointB.x || x < l.pointA.x));
        }, false)
    }

}
